/* eslint no-console: 0 */

import {
  DataSnapshot,
  off as dbRefOff,
  ref as getDatabaseRef,
  get as getDbValueOnce,
  onChildChanged,
} from 'firebase/database';
import debounce from 'lodash/debounce';
import React from 'react';

import {useFeatureData} from 'app/providers/FeatureDataProvider';
import {InvalidateOrderableScenes} from 'app/providers/OrderableScenesProvider';
import {useFirebaseDatabase} from 'app/tools/Firebase';

// After uploading a new feature, there can be a burst of realtime updates as new scenes
// for frequent sources like S2 are processed.
// To avoid overwhelming the app with requests,
// we debounce the updates so that if there are multiple updates within a short period of time,
// we only do one refresh at the end of the burst.
// We also do an immediate refresh at the beginning of the burst so that the user sees something happen right away.
// We also set a max wait time on the debounce so that if there are sustained updates, we still eventually refresh.
const DEBOUNCE_MS = 10_000;
const DEBOUNCE_MAX_WAIT_MS = 60_000;

interface DataSnapshotValue {
  [featureId: string]:
    | {
        data?: string;
        properties?: string;
      }
    // "string" only here for compatibility with the "data" and "properties" keys.
    | string
    | undefined;

  data?: string;
  properties?: string;
}

interface Props {
  featureCollectionId: number;
  refreshFeatureCollection: (id: number) => void | Promise<void>;
  invalidateFeature: (args: {
    featureId: number;
    featureCollectionId: number;
  }) => void | Promise<void>;
  invalidateOrderableScenes: InvalidateOrderableScenes;
  invalidateAgentExploreRuns: (featureId: number) => void;
  refreshFeatures: (clear?: boolean | undefined) => Promise<void>;
}

/**
 * Listens to the realtime channel and updates FeatureCollection and Features
 * with new data that comes in.
 *
 * Adapted from eventChannel.ts and saga.ts.
 *
 * Needs to be rendered underneath AuthProvider so that the firebase app is set
 * up.
 */
const FeatureCollectionMonitor: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  featureCollectionId,
  refreshFeatureCollection,
  invalidateFeature,
  invalidateOrderableScenes,
  invalidateAgentExploreRuns,
  refreshFeatures,
}) => {
  const database = useFirebaseDatabase();
  const {invalidateFeatureData: invalidateFeatureDataFromContext} = useFeatureData();

  // We keep the latest featureIds in a ref so that we don't have to unsubscribe
  // / resubscribe every time the user switches features (which we would have to
  // do if the listener was closing over a particular value of the prop).
  const refreshFeatureCollectionRef = React.useRef(refreshFeatureCollection);
  const invalidateFeatureRef = React.useRef(invalidateFeature);
  const invalidateFeatureDataRef = React.useRef(invalidateFeatureDataFromContext);
  const invalidateOrderableScenesRef = React.useRef(invalidateOrderableScenes);
  const invalidateAgentExploreRunsRef = React.useRef(invalidateAgentExploreRuns);
  const refreshFeaturesRef = React.useRef(refreshFeatures);

  React.useEffect(() => {
    refreshFeatureCollectionRef.current = refreshFeatureCollection;
    invalidateFeatureRef.current = invalidateFeature;
    invalidateFeatureDataRef.current = invalidateFeatureDataFromContext;
    invalidateOrderableScenesRef.current = invalidateOrderableScenes;
    invalidateAgentExploreRunsRef.current = invalidateAgentExploreRuns;
    refreshFeaturesRef.current = refreshFeatures;
  }, [
    refreshFeatureCollection,
    invalidateFeature,
    invalidateFeatureDataFromContext,
    invalidateOrderableScenes,
    invalidateAgentExploreRuns,
    refreshFeatures,
  ]);

  React.useEffect(() => {
    const featureCollectionRef = getDatabaseRef(
      database,
      `realtime/featureCollections/${featureCollectionId}`
    );

    // The last value for the entire featureCollection.
    let lastFcValue: DataSnapshotValue = {};

    type ProcessChangeFn = (
      childKey: string,
      childValue: NonNullable<DataSnapshotValue['']>
    ) => void;

    // Per-childKey leading + trailing debounced handlers.
    // Sustained realtime updates on a feature collapse to two fires
    // (one immediate, one with the latest value after the burst) instead of one per DEBOUNCE_MS window.
    const debouncedProcessByKey: Record<string, ProcessChangeFn & {cancel: () => void}> = {};

    const processChange: ProcessChangeFn = (childKey, childValue) => {
      const lastChildValue = lastFcValue[childKey];
      lastFcValue[childKey] = childValue;

      // These keys refer to changes in the feature collection.
      if (childKey === 'data' || childKey === 'properties') {
        // These will be datetime strings
        if (childValue !== lastChildValue) {
          console.info(`Received realtime update for featureCollection ${featureCollectionId}`);
          refreshFeatureCollectionRef.current(featureCollectionId);
        }
      } else if (typeof childValue === 'object') {
        const featureId = parseInt(childKey, 10);

        console.info(
          `Received realtime update for featureCollection ${featureCollectionId}, feature ${featureId}`
        );

        const needsDataUpdate =
          typeof lastChildValue !== 'object' || childValue.data !== lastChildValue.data;

        const needsPropertyUpdate =
          typeof lastChildValue !== 'object' || childValue.properties !== lastChildValue.properties;

        if (needsDataUpdate) {
          // Normally reloading all the data is a lot, but since FeatureData
          // comes through useCachedApiGet, we can invalidate it and then it
          // will only reload if there's a current component that's using it.
          invalidateFeatureDataRef.current(featureId);

          // Also reload the orderable scene data as a side effect when the data
          // for the target feature ID has changed.
          invalidateOrderableScenesRef.current(featureId);
          // Also read the agent explore runs as a side effect when the data
          // for the target feature ID has changed.
          invalidateAgentExploreRunsRef.current(featureId);
        }

        if (needsPropertyUpdate) {
          // update feature properties
          invalidateFeatureRef.current({featureCollectionId, featureId});
        }

        // lastChildValue being undefined signals that the feature was just created so
        // we want to refetch features so the new feature is in the feature list
        if (lastChildValue === undefined) {
          refreshFeaturesRef.current();
        }
      }
    };

    const handleChildChanged = (childSnapshot: DataSnapshot) => {
      // This will be either "data", "properties", or a feature ID.
      const childKey = childSnapshot.key;
      const childValue: NonNullable<DataSnapshotValue['']> = childSnapshot.val();

      // If childKey is null, we can't (1) access it on the `lastValue` object,
      // or (2) pass it through the `parseInt` method. Therefore, no-op.
      if (!childKey) {
        return;
      }

      let debounced = debouncedProcessByKey[childKey];
      if (!debounced) {
        debounced = debounce(processChange, DEBOUNCE_MS, {
          leading: true,
          trailing: true,
          maxWait: DEBOUNCE_MAX_WAIT_MS,
        });
        debouncedProcessByKey[childKey] = debounced;
      }
      debounced(childKey, childValue);
    };

    getDbValueOnce(featureCollectionRef).then((lastValueSnapshot) => {
      lastFcValue = lastValueSnapshot.val() || {};
    });

    console.info(`Subscribing to realtime channel for featureCollection ${featureCollectionId}`);
    onChildChanged(featureCollectionRef, handleChildChanged);

    return () => {
      console.info(
        `Unsubscribing from realtime channel for featureCollection ${featureCollectionId}`
      );
      Object.values(debouncedProcessByKey).forEach((d) => d.cancel());
      dbRefOff(featureCollectionRef, 'child_changed', handleChildChanged);
    };
  }, [database, featureCollectionId]);

  return null;
};

export default FeatureCollectionMonitor;
